import { gql } from "@/gql";

export const GET_SALON_ACCESS_STATE = gql(`
  query SalonAccessState($salonId: ID!) {
    salonAccessState(salonId: $salonId) {
      suspended
      reason
      source
      contactWhatsApp
    }
  }
`);

// Query to get salon details
export const GET_SALON = gql(`
  query GetSalon($salonId: String!) {
    getSalon(salonId: $salonId) {
      id
      name
      slug
      address
      additionalAddressInfo
      city
      state
      zipCode
      country
      timezone
      currency
      isActive
      createdAt
      updatedAt
      franchise {
        id
        name
        slug
      }
      contactInfo {
        id
        type
        value
        label
        order
        createdAt
        updatedAt
      }
    }
  }
`);

// Query to get only salon slug (lightweight query for settings/online features)
export const GET_SALON_SLUG = gql(`
  query GetSalonSlug($salonId: String!) {
    getSalon(salonId: $salonId) {
      id
      slug
    }
  }
`);

// Query storefront customization fields
export const GET_SALON_STOREFRONT = gql(`
  query GetSalonStorefront($salonId: String!) {
    getSalon(salonId: $salonId) {
      id
      heroBannerStorageKey
      heroBannerThumbnailStorageKey
      heroBannerUrl
      heroBannerThumbnailUrl
      announcementText
    }
  }
`);

export const SET_HERO_BANNER = gql(`
  mutation SetHeroBanner($salonId: String!, $storageKey: String!) {
    setHeroBanner(salonId: $salonId, storageKey: $storageKey) {
      heroBannerStorageKey
      heroBannerThumbnailStorageKey
    }
  }
`);

export const REMOVE_HERO_BANNER = gql(`
  mutation RemoveHeroBanner($salonId: String!) {
    removeHeroBanner(salonId: $salonId)
  }
`);

// Salon basic info mutation
export const UPDATE_SALON_BASIC_INFO = gql(`
  mutation UpdateSalonBasicInfo($salonId: String!, $input: UpdateSalonInput!) {
    updateSalon(salonId: $salonId, input: $input) {
      id
      name
      address
      additionalAddressInfo
      city
      state
      zipCode
      country
      updatedAt
    }
  }
`);

// Salon storefront customization mutation
export const UPDATE_SALON_STOREFRONT = gql(`
  mutation UpdateSalonStorefront($salonId: String!, $input: UpdateSalonInput!) {
    updateSalon(salonId: $salonId, input: $input) {
      id
      announcementText
      updatedAt
    }
  }
`);

// ---- Online-booking pause ----

// Pause state for the persistent banner + pause control. `bookingPausedUntil` is
// already sentinel-sanitized server-side (null when indefinite), so the UI renders
// "indefinitely" from `bookingPausedIndefinitely` and never a year-9999 date.
export const GET_SALON_PAUSE_STATE = gql(`
  query GetSalonPauseState($salonId: String!) {
    getSalon(salonId: $salonId) {
      id
      bookingPaused
      bookingPausedUntil
      bookingPausedIndefinitely
      bookingPausedMessage
    }
  }
`);

export const PAUSE_ONLINE_BOOKING = gql(`
  mutation PauseOnlineBooking($salonId: ID!, $input: PauseOnlineBookingInput!) {
    pauseOnlineBooking(salonId: $salonId, input: $input) {
      id
      bookingPaused
      bookingPausedUntil
      bookingPausedIndefinitely
      bookingPausedMessage
    }
  }
`);

export const RESUME_ONLINE_BOOKING = gql(`
  mutation ResumeOnlineBooking($salonId: ID!) {
    resumeOnlineBooking(salonId: $salonId) {
      id
      bookingPaused
      bookingPausedUntil
      bookingPausedIndefinitely
      bookingPausedMessage
    }
  }
`);

// Client-facing WhatsApp number (for the storefront wa.me contact CTA).
export const GET_SALON_WHATSAPP = gql(`
  query GetSalonWhatsapp($salonId: String!) {
    getSalon(salonId: $salonId) {
      id
      whatsappNumber
    }
  }
`);

export const UPDATE_SALON_WHATSAPP = gql(`
  mutation UpdateSalonWhatsapp($salonId: String!, $input: UpdateSalonInput!) {
    updateSalon(salonId: $salonId, input: $input) {
      id
      whatsappNumber
      updatedAt
    }
  }
`);

export const GET_SALON_SOCIAL_LINKS = gql(`
  query GetSalonSocialLinks($salonId: String!) {
    getSalon(salonId: $salonId) {
      id
      socialLinks {
        platform
        url
      }
    }
  }
`);

export const UPDATE_SALON_SOCIAL_LINKS = gql(`
  mutation UpdateSalonSocialLinks($salonId: String!, $input: UpdateSalonSocialLinksInput!) {
    updateSalonSocialLinks(salonId: $salonId, input: $input) {
      platform
      url
    }
  }
`);

// Salon contact info mutations
export const CREATE_SALON_CONTACT = gql(`
  mutation CreateSalonContact($salonId: String!, $input: CreateSalonContactInput!) {
    createSalonContact(salonId: $salonId, input: $input) {
      id
      type
      value
      label
      order
      createdAt
      updatedAt
    }
  }
`);

export const UPDATE_SALON_CONTACT = gql(`
  mutation UpdateSalonContact($salonId: String!, $input: UpdateSalonContactInput!) {
    updateSalonContact(salonId: $salonId, input: $input) {
      id
      type
      value
      label
      order
      updatedAt
    }
  }
`);

export const DELETE_SALON_CONTACT = gql(`
  mutation DeleteSalonContact($salonId: String!, $contactId: String!) {
    deleteSalonContact(salonId: $salonId, contactId: $contactId)
  }
`);

export const BULK_UPDATE_SALON_CONTACTS = gql(`
  mutation BulkUpdateSalonContacts($salonId: String!, $input: BulkUpdateSalonContactsInput!) {
    bulkUpdateSalonContacts(salonId: $salonId, input: $input) {
      id
      type
      value
      label
      order
      createdAt
      updatedAt
    }
  }
`);

// Query salon contacts
export const GET_SALON_CONTACTS = gql(`
  query GetSalonContacts($type: SalonContactType) {
    salonContacts(type: $type) {
      id
      type
      value
      label
      order
      createdAt
      updatedAt
    }
  }
`);
